// @flow
import $ from 'jquery'
import _ from 'lodash'
import React, { Component } from 'react'
import GoogleMap from './MicrolocationsMap/google.map'
import PointEditor from './MicrolocationsMap/PointEditor'
import styled, { type StyledComponent } from 'styled-components'
import { blueLinkDashed } from './AdEdit/controls'
import type { Point } from './MicrolocationsMap/google.map'
import type { GoogleMapPoint } from './MicrolocationsMap/google.map.point'
import { audienceInputsWidth } from './constants'
import ImportFromFile from './MicrolocationsMap/ImportFromFile'
import SearchBox from './MicrolocationsMap/SearchBox'

export const Help: StyledComponent<Object, any, any> = styled.p`
  width: ${audienceInputsWidth}px;
`
const Tools: StyledComponent<Object, any, any> = styled.div`
  margin: 16px 0 0 0;
`
const Counter: StyledComponent<Object, any, any> = styled.span`
  display: inline-block;
  padding-left: 20px;
  padding-right: 20px;
`
const Editors: StyledComponent<Object, any, any> = styled.div`
  padding-top: 10px;
`
const Wrapper: StyledComponent<Object, any, any> = styled.div`
  .canvas {
    height: 350px;
    width: ${audienceInputsWidth}px;
  }

  a {
    ${blueLinkDashed()};
  }

  width: ${audienceInputsWidth}px;
`

const t = {
  km: 'км',
  editor_for_only_point: 'Для редактирования области сначала выберите её на карте',
  clear_map: 'Удалить все точки',
  points_count: 'Выбрано точек на карте: ',
  sure_to_clear_map: 'Вы уверены, что хотите удалить все точки?'
}

const editorsCountLimit = 10

const minRadius = 0.1
const searchBoxMinRadius = 3
const maxRadius = 10

type Props = {|
  defaultValue: Array<Point>,
  onChange: (Array<Point>) => void
|}

type State = {|
  pointsCount: number,
  selectedPoint: ?GoogleMapPoint
|}

export default class MicrolocationsMap extends Component<Props, State> {
  lastSelectedPoint = null
  microlocationSavedToField = true

  canvas: any
  map: GoogleMap

  state = {
    pointsCount: 0,
    selectedPoint: null
  }

  render() {
    return (
      <Wrapper className="t-MicrolocationsMap">
        <div
          className="canvas"
          ref={(el) => {
            this.canvas = el
          }}
        />
        <SearchBox onSelect={(point) => this.searchLocationAdded(point)} />
        <Tools>
          <ImportFromFile
            minRadius={minRadius}
            maxRadius={maxRadius}
            onLoad={(points, message) => this.pointsImported(points, message)}
          />
          <Counter>
            {t.points_count} {this.state.pointsCount}
          </Counter>
          <button
            className="btn btn-default btn-sm"
            onClick={(e) => {
              this.clearMap(e)
            }}
          >
            {t.clear_map}
          </button>
        </Tools>
        <Editors>
          {this.state.pointsCount > editorsCountLimit && <label>{t.editor_for_only_point}</label>}
          {this.state.pointsCount > editorsCountLimit
            ? this.state.selectedPoint && this.renderPointEditor(this.state.selectedPoint)
            : this.map && this.map.points().map((point) => this.renderPointEditor(point))}
        </Editors>
      </Wrapper>
    )
  }

  renderPointEditor(point: GoogleMapPoint) {
    return (
      <PointEditor
        key={point.id}
        point={point}
        minValue={minRadius}
        maxValue={maxRadius}
        units={t.km}
        onCoordClick={() => this.focusPoint(point)}
        onCoordMouseOver={() => this.accentPoint(point)}
        onCoordMouseOut={() => this.forgetPoint(point)}
        onRadiusChange={(radius: number) => this.changePointRadius(point, radius)}
        onDeleteClick={() => this.deletePoint(point)}
        onNameChange={(name) => this.changePointName(point, name)}
      />
    )
  }

  focusPoint(point: GoogleMapPoint) {
    this.map.focusOn(point.longitude, point.latitude)
    this.selectPoint(point)
  }

  accentPoint(point: GoogleMapPoint) {
    if (this.lastSelectedPoint === point) return
    point.useBrightView()
  }

  forgetPoint(point: GoogleMapPoint) {
    if (this.lastSelectedPoint === point) return
    point.useDefaultView()
  }

  changePointRadius(point: GoogleMapPoint, radius: number) {
    point.setRadius(radius)
    this.updateField(this.map)
  }

  changePointName(point: GoogleMapPoint, name: string) {
    point.setName(name)
    this.updateField(this.map)
  }

  deletePoint(point: GoogleMapPoint) {
    this.map.removePoint(point)
    this.updateField(this.map)
    if (this.lastSelectedPoint === point) {
      this.lastSelectedPoint = null
      this.setState({ selectedPoint: null })
    }
  }

  componentDidMount() {
    let map = new GoogleMap($(this.canvas))
    map.onPointAdd((point) => {
      this.pointAdded(map, point)
    })

    this.addPoints(map, this.props.defaultValue)
    map.focus()
    this.map = map
  }

  clearMap(e: any) {
    e.preventDefault()
    if (window.confirm(t.sure_to_clear_map)) {
      this.map.clearAll()
      this.updateField(this.map)
      this.lastSelectedPoint = null
      this.setState({ selectedPoint: null })
    }
  }

  updateField(map: GoogleMap) {
    this.microlocationSavedToField = false
    this.setState({ pointsCount: map.pointsCount() })
    let _this = this
    setTimeout(() => {
      if (!_this.microlocationSavedToField) {
        this.props.onChange(map.pointsData())
        this.microlocationSavedToField = true
      }
    })
  }

  pointAdded(map: GoogleMap, point: GoogleMapPoint) {
    this.updateField(map)

    point.onClick(() => {
      this.selectPoint(point)
    })
    point.onChange(() => {
      this.selectPoint(point)
    })

    this.selectPoint(point)
  }

  addPoints(map: GoogleMap, pointsData: Array<Point>) {
    _.map(pointsData, (p) => map.addPoint(p))
  }

  // for external use
  addPoint(point: Point) {
    this.addPoints(this.map, [point])
  }

  selectPoint(point: GoogleMapPoint) {
    if (this.lastSelectedPoint) this.lastSelectedPoint.useDefaultView()
    point.useBrightView()
    this.lastSelectedPoint = point
    this.setState({ selectedPoint: point })
  }

  pointsImported(points: Array<Point>, message: string) {
    this.addPoints(this.map, points)
    window.alert(message)
    this.map.focus()
  }

  searchLocationAdded(point: Point) {
    const pointToAdd = _.cloneDeep(point)
    pointToAdd.radius = _.max([
      minRadius,
      searchBoxMinRadius,
      _.min([maxRadius, _.ceil(point.radius * 10) / 10.0])
    ])
    this.addPoints(this.map, [pointToAdd])
    if (this.lastSelectedPoint) this.focusPoint(this.lastSelectedPoint)
  }
}
